import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Paper, Button, Typography, LinearProgress, Grid2, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { IEvento } from '../../../../commons/types/eventos';
import styled from 'styled-components';
import { createHTMLFromString } from '../../../PaginasDinamicas';
import { ptBR, esES } from "@mui/material/locale";
import { useTranslation } from '../../../../commons/useTranslasion';
import { useDateFormatters } from '../../../../utils/useDateFormatters';

interface ProximosEventosProps {
  eventos: IEvento[];
  eventosQuery: { dataInicio: Date | Dayjs | null; dataFim: Date | Dayjs | null; };
  loading: boolean;
  handleDateChange: (field: 'dataInicio' | 'dataFim', value: Dayjs | null) => void;
  handleFilterEvents: () => void;
}

const ProximosEventos: React.FC<ProximosEventosProps> = ({
  eventos, eventosQuery, loading, handleDateChange, handleFilterEvents
}) => {

  const { translate, language } = useTranslation();
  const { formatoDataDayjs, localeDayjs, formatarDataHora } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  const getMinDataFim = () => {
    return eventosQuery.dataInicio ? dayjs(eventosQuery.dataInicio) : dayjs();
  };

  const getDataEvento = (evento: IEvento) => {
    const dataInicio = formatarDataHora(evento.dtInicio);
    const horaFim = dayjs(evento.dtFim).format('HH:mm');
    return `${dataInicio} - ${horaFim}`;
  }

  return (
    <Grid2 container spacing={2} alignItems="center">
      <Grid2 container>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeDayjs}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <DesktopDatePicker
              value={eventosQuery.dataInicio ? dayjs(eventosQuery.dataInicio) : null}
              onChange={(newValue) => handleDateChange('dataInicio', newValue)}
              label={translate('dataInicio')}
              format={formatoDataDayjs}
              minDate={dayjs()}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <DesktopDatePicker
              value={eventosQuery.dataFim ? dayjs(eventosQuery.dataFim) : null}
              onChange={(newValue) => handleDateChange('dataFim', newValue)}
              label={translate('dataFim')}
              format={formatoDataDayjs}
              minDate={getMinDataFim()}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <ButtonFilter onClick={handleFilterEvents}>{translate('filtrar')}</ButtonFilter>
          </Grid2>
        </LocalizationProvider>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        {loading
          ? (<>
            <LinearProgress sx={{ width: '100%' }} />
          </>)
          : eventos.length == 0
            ? (<Typography variant="body1">{translate('nenhumEventoEncontrado')}</Typography>)
            : (
              <Grid2 container spacing={1}>
                {eventos.map((evento) => (
                  <Grid2 size={{ xs: 12, lg: 8 }}>
                    <Accordion key={evento.id} sx={{ width: '100%' }}>
                      <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h6">{evento.titulo}</Typography>
                            {dayjs(evento.dtInicio).isBefore(dayjs()) && (
                              <Tooltip title="Evento já encerrado" placement="top">
                                <WarningOutlinedIcon color="warning" />
                              </Tooltip>
                            )}
                          </div>
                          <Grid2 container spacing={1}>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                              <CalendarMonthRoundedIcon fontSize="small" />
                              <Typography variant="body2">{getDataEvento(evento)}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationOnRoundedIcon fontSize="small" />
                              <Typography variant="body2">{evento.modoEvento === 'Online' ? 'Online' : 'Presencial'}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                              <RecordVoiceOverRoundedIcon />
                              <Typography variant="body2">{translate('palestrante')}: {evento.palestrante}</Typography>
                            </Grid2>
                          </Grid2>
                        </div>
                      </AccordionSummaryStyled>
                      <AccordionDetails>
                        <Paper elevation={3} style={{ padding: '20px', marginBottom: '10px' }}>
                          <Grid2 container spacing={1}>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 12 }}>
                              <Typography variant="h6" sx={{ width: '100%' }}>{evento.titulo}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 12 }}>
                              <CalendarMonthRoundedIcon fontSize="small" />
                              <Typography variant="body2">{getDataEvento(evento)}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 12 }}>
                              <LocationOnRoundedIcon fontSize="small" />
                              <Typography variant="body2">{evento.modoEvento === 'Online' ? translate('online') : translate('presencial')}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 12 }}>
                              <RecordVoiceOverRoundedIcon />
                              <Typography variant="body2">{translate('palestrante')}: {evento.palestrante}</Typography>
                            </Grid2>
                            <Grid2 sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 12 }}>
                              <div style={{ width: '100%' }}>
                                <Typography variant="body1">{translate('descricao')}</Typography>
                                {createHTMLFromString(evento.descricao)}
                              </div>
                            </Grid2>
                          </Grid2>
                          <a href={evento.linkEvento} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: '10px' }}>
                            {translate('verEvento')}
                          </a>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </Grid2>
                ))}
              </Grid2>
            )
        }
      </Grid2>
    </Grid2>
  );
};

const ButtonFilter = styled(Button)`
  color: #414651 !important;
  border-color: #d5d7da !important;
  text-transform: none !important;
`;

const AccordionSummaryStyled = styled(AccordionSummary)`
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
`;


export default ProximosEventos;
